// ── 출퇴근 달력 유틸 함수 및 상수 ─────────────────────────────────────────────
import { todayKST, dayOfWeekUTC, daysInMonth, addDaysUtc } from '../../../utils/date';

/** 시스템 도입 이전 날짜는 표시하지 않음 (2026-03-09 이전은 홈페이지 출퇴근 미사용) */
export const CALENDAR_START_YMD = '2026-03-09';

/** 상태 코드 → { 한글, 색상(배경), 텍스트색 } */
export const STATUS_META = {
  present:      { label: '출근',      bg: '#dcfce7', text: '#166534', border: '#86efac' },
  missing_out:  { label: '미퇴근',    bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  absent:       { label: '결근',      bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  on_leave:     { label: '연차',      bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  half_leave:   { label: '반차',      bg: '#ede9fe', text: '#5b21b6', border: '#c4b5fd' },
  offday:       { label: '휴무',      bg: 'var(--bg-subtle)', text: 'var(--text-muted)', border: 'var(--border)' },
  offday_work:  { label: '휴무근무',  bg: '#fff7ed', text: '#c2410c', border: '#fdba74' },
  holiday:      { label: '공휴일',    bg: '#fce7f3', text: '#9d174d', border: '#f9a8d4' },
  holiday_work: { label: '공휴일근무', bg: '#fdf2f8', text: '#7e22ce', border: '#e879f9' },
  // 예정: 실기록과 확실히 구분되게 옅은 회색 (2026-09-01 A안 — 근무유형 기반 자동 표시일 뿐임을 시각화)
  scheduled:    { label: '예정',      bg: 'var(--bg-card)', text: 'var(--text-muted)', border: 'var(--border)' },
};

/** 요일 이름 */
export const WEEKDAY_NAMES = { sun: '일', mon: '월', tue: '화', wed: '수', thu: '목', fri: '금', sat: '토' };
/** 일요일 시작 (일~토) */
export const WEEKDAY_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

// ── 스타일 상수 ───────────────────────────────────────────────────────────────
const baseInputStyle = {
  padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
  fontSize: 13, outline: 'none', background: 'var(--bg-card)',
};
const baseBtnStyle = {
  padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
};
export const styles = {
  baseInputStyle,
  baseBtnStyle,
  primaryBtn: { ...baseBtnStyle, background: '#3b82f6', color: '#fff' },
  dangerBtn:  { ...baseBtnStyle, background: '#ef4444', color: '#fff' },
  grayBtn:    { ...baseBtnStyle, background: 'var(--border)', color: 'var(--text-sub)' },
  greenBtn:   { ...baseBtnStyle, background: '#22c55e', color: '#fff' },
};

// ── 유틸 함수 ────────────────────────────────────────────────────────────────

/** YYYY-MM-DD에 n일 더하기
 * addDaysUtc: utils/date.js 사용 (new Date/toISOString 직접 사용 금지) */
export function addDays(ymd, n) {
  return addDaysUtc(ymd, n);
}

/** YYYY-MM-DD → 해당 주 일요일 (일요일 시작 기준)
 * dayOfWeekUTC: utils/date.js 사용 (new Date 직접 사용 금지) */
export function getWeekSunday(ymd) {
  const day = dayOfWeekUTC(ymd); // 0=일
  return addDays(ymd, -day);
}

/** 현재 KST 날짜
 * todayKST: utils/date.js 사용 (new Date 직접 사용 금지) */
export function getTodayYmd() {
  return todayKST();
}

/**
 * "HH:mm" 문자열 → 하루(24시간) 기준 백분율(0~100)
 * 타임라인 배경 바 위치 계산에 사용
 */
export function timeToPercent(timeStr) {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? 0);
  if (isNaN(h) || isNaN(m)) return null;
  return ((h * 60 + m) / (24 * 60)) * 100;
}

/** n개월 전후 이동 */
export function shiftMonth(year, month, delta) {
  let y = year;
  let m = month + delta;
  while (m < 1)  { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return { year: y, month: m };
}

/** 해당 월 달력 그리드용 날짜 배열 (앞뒤 빈칸 포함, 7의 배수)
 * dayOfWeekUTC, daysInMonth: utils/date.js 사용 (new Date 직접 사용 금지) */
export function buildMonthGrid(year, month) {
  const ymd01 = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDayNum = daysInMonth(year, month);
  // 첫째 날 요일 (0=일 → 일요일 시작이므로 그대로 사용)
  const startDow = dayOfWeekUTC(ymd01);
  const grid = [];
  for (let i = 0; i < startDow; i++) grid.push(null);
  for (let d = 1; d <= lastDayNum; d++) {
    grid.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
}

/**
 * 시간 입력 자동 포맷: 숫자만 입력해도 HH:mm 형식으로 변환
 * 예) "1800" → "18:00", "900" → "09:00"
 * 공용 finalizeTime 위임 — 콜론 포함 입력("9:5")의 시/분 개별 정규화 포함 ("00:95" 버그 수정)
 */
export { finalizeTime as autoFormatTime } from '../../../utils/timeInput';
